import type { DirectoryPublic } from '@repo/types/directory';
import type { FilePublicWithPermissions } from '@repo/types/files';

import { useCallback, useMemo, useRef } from 'react';

import useLongPress from '@/hooks/long-press/useLongPress';
import { useFilesStorage } from '@/store/files-store';

type FSEntityType = 'file' | 'directory';

interface UseFSEntityInteractionProps<T extends FilePublicWithPermissions | DirectoryPublic> {
  entity: T;
  entityType: FSEntityType;
  onOpen?: () => void;
}

export function useFSEntityInteraction<T extends FilePublicWithPermissions | DirectoryPublic>(useFSEntityInteractionProps: UseFSEntityInteractionProps<T>) {
  const { entity, entityType, onOpen } = useFSEntityInteractionProps;

  const onFSEntityClick = useFilesStorage((state) => state.onFSEntityClick);
  const isEntitySelected = useFilesStorage((state) => state.isSelected(entity.id, entityType));
  const size = useFilesStorage((state) => state.selected.files.length + state.selected.directories.length);
  const focusedIndex = useFilesStorage((state) => state.focusedIndex);
  const allItems = useFilesStorage((state) => state.allItems);

  // Check if this entity is currently focused
  const isFocused = useMemo<boolean>(() => {
    if (focusedIndex === null || focusedIndex < 0) return false;
    const focusedItem = allItems[focusedIndex];
    return focusedItem?.id === entity.id;
  }, [focusedIndex, allItems, entity.id]);

  // Get the index of this entity in allItems
  const entityIndex = useMemo<number>(() => {
    return allItems.findIndex((item) => item.id === entity.id);
  }, [allItems, entity.id]);

  // Track whether the last pointer interaction was touch, and whether a long-press fired
  const lastInputWasTouchRef = useRef<boolean>(false);
  const longPressTriggeredRef = useRef<boolean>(false);

  // Long-press should select/toggle selection on both touch and mouse
  const handleHold = useCallback(
    (event: React.PointerEvent<HTMLDivElement>, isTouch: boolean) => {
      longPressTriggeredRef.current = true;
      lastInputWasTouchRef.current = isTouch;
      onFSEntityClick({
        [entityType]: entity,
        event,
        mode: 'ensure-selected',
        itemIndex: entityIndex,
      });
    },
    [onFSEntityClick, entity, entityType, entityIndex]
  );

  const baseLongPressHandlers = useLongPress(handleHold, {
    preventDefault: true,
  });

  // Wrap pointer handlers to record pointer type and reset flags
  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      lastInputWasTouchRef.current = event.pointerType === 'touch';
      longPressTriggeredRef.current = false;
      baseLongPressHandlers.onPointerDown(event);
    },
    [baseLongPressHandlers]
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      baseLongPressHandlers.onPointerMove(event);
    },
    [baseLongPressHandlers]
  );

  const onPointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      baseLongPressHandlers.onPointerUp(event);
    },
    [baseLongPressHandlers]
  );

  // Click behavior:
  // - Touch: open (unless it was a long-press, which we've already handled)
  // - Mouse: select
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement> | React.PointerEvent<HTMLDivElement>) => {
      if (longPressTriggeredRef.current) {
        longPressTriggeredRef.current = false;
        return;
      }

      if (lastInputWasTouchRef.current) {
        if (size > 0) {
          onFSEntityClick({
            [entityType]: entity,
            event,
            mode: 'toggle',
            itemIndex: entityIndex,
          });
          return;
        }

        if (entity.permissions.read && onOpen) {
          onOpen();
        }
        return;
      }

      onFSEntityClick({
        [entityType]: entity,
        event,
        mode: 'replace-or-clear',
        itemIndex: entityIndex,
      });
    },
    [entity, entityType, onOpen, onFSEntityClick, size, entityIndex]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && entity.permissions.read && onOpen) {
        onOpen();
      }
    },
    [entity, onOpen]
  );

  const handleDoubleClick = useCallback(() => {
    if (entity.permissions.read && onOpen) {
      onOpen();
    }
  }, [entity, onOpen]);

  const longPressHandlers = useMemo(
    () => ({
      onPointerDown,
      onPointerMove,
      onPointerUp,
    }),
    [onPointerDown, onPointerMove, onPointerUp]
  );

  return useMemo(
    () => ({
      isEntitySelected,
      isFocused,
      size,
      handleClick,
      handleKeyDown,
      handleDoubleClick,
      longPressHandlers,
    }),
    [isEntitySelected, isFocused, size, handleClick, handleKeyDown, handleDoubleClick, longPressHandlers]
  );
}
